import { useEffect, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { searchTransactions } from '../../../Module/Settlement/Presentation/settlementActions';
import {
  selectTransactionSearchStatus,
  selectTransactionSearchResults,
  selectTransactionSearchMeta
} from '../../../Module/Settlement/Presentation/settlementSelectors';
import { clearTransactionSearch } from '../../../Module/Settlement/Presentation/settlementSlice';
import defaultProfileIcon from '../../../assets/Icon/project/default-profile.png';
import { selectSelectedProject, selectProjects } from '../../../Module/Project/Presentation/projectSelectors';
import { getCloudFrontImageUrl } from '../../../Utils/cloudFrontImage';
import type { 
  TransactionSearchItem, 
  TransactionSearchMeta 
} from '../../../Module/Settlement/Domain/entity/SettlementTypes';
import { projectUseCases } from '../../../Module/Project/Presentation/projectUseCases';
import { settlementUseCases } from '../../../Module/Settlement/Presentation/settlementUseCases';
import type { TFunction } from 'i18next';
import { normalizeCurrencyCode } from '../../../Utils/formatCurrency';

export type DonorSummary = {
  total: number;       // 총 후원 신청자
  cancelled: number;   // 후원 중단자
  failed: number;      // 등록 실패자
  inProgress: number;  // 후원 진행자
};

export type DonorPaymentMethod = 'cms' | 'card';
export type DonorStatusCode = 'complete' | 'fail' | 'cancel';

export type DonorItem = {
  id: string;
  name: string;
  method: DonorPaymentMethod;
  status: DonorStatusCode;
  email: string;
  phone: string;
  date: string;
  message: string;
  address: string;
  donationType: string;
  amount: number;
  currencyType: 'KRW' | 'USD';
  count: number;
  profileImg: string;
};

const getDonorPaymentMethod = (item: TransactionSearchItem): DonorPaymentMethod => {
  const methodValues = [
    item.sponsor_method_type,
    item.payment_method,
    item.payment_method_type,
    item.payment_type,
    item.pay_type,
    item.method_type,
    item.sponsor_method,
    item.pay_method,
    item.paymentMethod,
    item.paymentKind,
    item.payment_kind,
    item.old_cms_from,
  ]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);
  const normalizedMethods = methodValues.map((value) => value.toUpperCase());

  if (normalizedMethods.some((m) => m === 'CRE' || m.includes('CARD') || m.includes('CREDIT') || m.includes('CC') || m.includes('THEBILL'))) {
    return 'card';
  }
  if (item.card_holder || item.name_card || item.card_number) return 'card';
  return 'cms';
};

const getTransactionDonorStatus = (resultCode?: string): DonorStatusCode => {
  const r = (resultCode ?? '').toLowerCase();
  if (r === 'cancelled' || r === 'cancel') return 'cancel';
  if (r.includes('fail')) return 'fail';
  return 'complete';
};

const formatDisplayDate = (rawDate?: string) => {
  if (!rawDate) return '';
  const dateObj = new Date(rawDate);
  if (Number.isNaN(dateObj.getTime())) return '';
  return `${dateObj.getFullYear()}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${String(dateObj.getDate()).padStart(2, '0')}`;
};

const buildDonorName = (item: TransactionSearchItem, anonymousLabel: string) => {
  const parts = [item.first_name, item.last_name].filter((p) => String(p ?? '').trim().length > 0);
  if (parts.length === 0) return anonymousLabel;
  return parts.join('');
};

const mapTransactionItemsToDonorItems = (
  searchResults: TransactionSearchItem[],
  currentProject: { type?: string; currency_type?: string } | undefined,
  t: TFunction,
): DonorItem[] => {
  if (searchResults.length === 0) return [];

  const isOneTime = ['OT', 'ONCE', 'ONE_TIME', 'ONETIME'].includes((currentProject?.type ?? '').toUpperCase());
  const donationType = isOneTime ? t('page.newProject.review.fundingType.once') : t('page.newProject.review.fundingType.regular');

  return searchResults.map((item, index) => {
    const status = getTransactionDonorStatus(item.result_code);
    const formattedDate = formatDisplayDate(item.created_at);
    const thumb = item.thumbnail
      ? (getCloudFrontImageUrl(item.thumbnail, '84x84') || item.thumbnail)
      : defaultProfileIcon;
    const currencyType = normalizeCurrencyCode(item.currency_type ?? currentProject?.currency_type);

    return {
      id: item.transaction_id ?? `donor-${index}`,
      name: buildDonorName(item, t('common.labels.anonymous')),
      method: getDonorPaymentMethod(item),
      status,
      email: item.email ?? '',
      phone: item.phone ?? '',
      date: formattedDate,
      message: item.comment ?? '',
      address: '',
      donationType,
      amount: Number(item.sponsor_amount) || 0,
      currencyType,
      count: 1,
      profileImg: thumb,
    };
  });
};

const toNonNegativeCount = (value: unknown) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
};

export const useDonorManagement = (targetProjectId?: string) => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();

  const selectedProject = useAppSelector(selectSelectedProject);
  const projects = useAppSelector(selectProjects);
  
  const projectId = useMemo(() => {
    if (targetProjectId) return targetProjectId;
    
    if (selectedProject?.project_id) {
      return selectedProject.project_id;
    }
    if (projects.length > 0 && projects[0].project_id) {
      return projects[0].project_id;
    }
    return null;
  }, [targetProjectId, selectedProject, projects]);

  const currentProject = useMemo(() => {
    if (!projectId) return undefined;
    return projects.find(p => p.project_id === projectId || p._id === projectId);
  }, [projectId, projects]);

  const searchStatus = useAppSelector(selectTransactionSearchStatus);
  const searchResults = useAppSelector(selectTransactionSearchResults) as TransactionSearchItem[] | undefined;
  const searchMeta = useAppSelector(selectTransactionSearchMeta) as TransactionSearchMeta | undefined;

  const [donorSumFromApi, setDonorSumFromApi] = useState<{
    active: number;
    cancelled: number;
    failed: number;
  } | null>(null);

  useEffect(() => {
    if (!projectId) {
      setDonorSumFromApi(null);
      return;
    }
    setDonorSumFromApi(null);
    let cancelled = false;
    projectUseCases.sumProjectDonors
      .execute({ project_id: projectId })
      .then((res) => {
        if (cancelled) return;
        const resAny = res as Record<string, unknown>;
        const statusCode = resAny.statusCode ?? resAny.status_code;
        if (statusCode !== undefined && Number(statusCode) !== 200 && Number(statusCode) !== 0) {
          setDonorSumFromApi(null);
          return;
        }
        setDonorSumFromApi({
          active: toNonNegativeCount(res.active),
          cancelled: toNonNegativeCount(res.cancelled),
          failed: toNonNegativeCount(res.failed),
        });
      })
      .catch(() => {
        if (!cancelled) setDonorSumFromApi(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const hasMore = useMemo(() => {
    const total = searchMeta?.totalElements ?? 0;
    const loaded = searchResults?.length ?? 0;
    return loaded < total;
  }, [searchMeta?.totalElements, searchResults?.length]);

  const isLoadingMore = searchStatus === 'searching' && (searchResults?.length ?? 0) > 0;

  const LIMIT = 100;

  const fetchDonors = useCallback((offset = 0, append = false) => {
    if (!projectId) return;
    
    dispatch(searchTransactions({
      params: [
        { fld: 'projectId', val: projectId, op: 'eq' }
      ],
      sort: 'createdAt:desc',
      limit: LIMIT,
      offset,
      append
    }));
  }, [dispatch, projectId]);

  const loadMoreDonors = useCallback(() => {
    if (!projectId || isLoadingMore || !hasMore) return;
    const currentLength = searchResults?.length ?? 0;
    fetchDonors(currentLength, true);
  }, [projectId, fetchDonors, searchResults?.length, isLoadingMore, hasMore]);

  useEffect(() => {
    if (projectId) {
      dispatch(clearTransactionSearch());
      fetchDonors(0, false);
    }
  }, [projectId, dispatch, fetchDonors]);

  useEffect(() => {
    return () => {
      dispatch(clearTransactionSearch());
    };
  }, [dispatch]);

  const allDonors: DonorItem[] = useMemo(
    () => mapTransactionItemsToDonorItems(searchResults ?? [], currentProject, t),
    [searchResults, t, currentProject],
  );

  const fetchActiveDonorsAllPages = useCallback(async (): Promise<DonorItem[]> => {
    if (!projectId) return [];
    const PAGE = 100;
    const collected: TransactionSearchItem[] = [];
    let offset = 0;

    for (;;) {
      const res = await settlementUseCases.searchTransactions.execute({
        params: [{ fld: 'projectId', val: projectId, op: 'eq' }],
        sort: 'createdAt:desc',
        limit: PAGE,
        offset,
      });
      const batch = res.data ?? [];
      if (batch.length === 0) break;
      collected.push(...batch);
      const totalElements = res.meta?.totalElements ?? collected.length;
      if (collected.length >= totalElements) break;
      offset += batch.length;
    }

    const mapped = mapTransactionItemsToDonorItems(collected, currentProject, t);
    return mapped.filter((d) => d.status !== 'cancel');
  }, [projectId, currentProject, t]);

  const donors = useMemo(
    () => allDonors.filter((d) => d.status !== 'cancel'),
    [allDonors]
  );

  const summary: DonorSummary = useMemo(() => {
    if (donorSumFromApi) {
      const { active, cancelled, failed } = donorSumFromApi;
      return {
        total: active + cancelled + failed,
        cancelled,
        failed,
        inProgress: active,
      };
    }

    const totalElements = searchMeta?.totalElements ?? 0;
    const total = totalElements > 0 ? totalElements : allDonors.length;
    let cancelled = 0;
    let failed = 0;
    let inProgress = 0;
    for (const d of allDonors) {
      if (d.status === 'cancel') cancelled++;
      else if (d.status === 'fail') failed++;
      else inProgress++;
    }

    return {
      total,
      cancelled,
      failed,
      inProgress
    };
  }, [allDonors, donorSumFromApi, searchMeta?.totalElements]);

  const hasData = donors.length > 0;

  const isLoading = searchStatus === 'searching' && (searchResults?.length ?? 0) === 0;

  const pagination = useMemo(() => ({
    totalElements: searchMeta?.totalElements ?? 0,
    totalPages: searchMeta?.totalPages ?? 0,
    currentPage: Math.floor((searchMeta?.msearch?.offset ?? 0) / (searchMeta?.perPage ?? 10)) + 1,
    perPage: searchMeta?.perPage ?? 10
  }), [searchMeta]);

  return {
    summary,
    donors,
    hasData,
    
    isLoading,
    isLoadingMore,
    searchStatus,
    
    pagination,
    hasMore,
    loadMoreDonors,
    
    fetchDonors,
    fetchActiveDonorsAllPages,
    
    projectId
  };
};

export default useDonorManagement;
